// Shared unit creation utilities
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Firestore,
  Timestamp,
} from 'firebase/firestore';

/** Max allowed `defaultCreditBalanceOnUnitCreate` in platform settings (aligned with Firestore rules). */
export const MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE = 100_000;

/**
 * Read `systemSettings/platform.defaultCreditBalanceOnUnitCreate` (integer credits, 0 or positive).
 */
export function parseDefaultCreditBalanceOnUnitCreate(
  platformData: Record<string, unknown> | undefined,
): number {
  if (!platformData) return 0;
  const raw = platformData.defaultCreditBalanceOnUnitCreate;
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n >= 0 && n <= MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE) {
      return n;
    }
  }
  return 0;
}

export interface UnitCreationData {
  name: string;
  unitNumber: string;
  userId: string;
  userEmail: string;
  termsAcceptedAt: Date;
  additionalData?: Record<string, any>;
}

export interface ExistingUnit {
  id: string;
  name: string;
  unitNumber: string;
  status: string;
}

export interface UnitDuplicateCheckResult {
  exists: boolean;
  unit?: ExistingUnit;
  duplicateType?: 'name' | 'number';
}

/**
 * Check if a unit with the same name or number already exists
 * Only considers approved and pending units (declined units are excluded)
 */
export async function checkUnitDuplicate(
  db: Firestore,
  unitName: string,
  unitNumber: string
): Promise<UnitDuplicateCheckResult> {
  try {
    const nameQuery = query(
      collection(db, 'units'),
      where('name', '==', unitName.trim())
    );
    const numberQuery = query(
      collection(db, 'units'),
      where('unitNumber', '==', unitNumber.trim())
    );

    const [nameSnapshot, numberSnapshot] = await Promise.all([
      getDocs(nameQuery),
      getDocs(numberQuery),
    ]);

    let existingUnit: ExistingUnit | null = null;
    let duplicateType: 'name' | 'number' | undefined;

    // Check name duplicates (only consider approved and pending units, exclude denied/declined)
    for (const docSnap of nameSnapshot.docs) {
      const data = docSnap.data();
      const reviewStatus = data.reviewStatus || 'approved'; // Legacy units are considered approved
      // Explicitly exclude denied units from duplicate check
      if (reviewStatus === 'approved' || reviewStatus === 'pending') {
        existingUnit = {
          id: docSnap.id,
          name: data.name as string,
          unitNumber: data.unitNumber as string,
          status: reviewStatus,
        };
        duplicateType = 'name';
        break;
      }
    }

    // Check number duplicates if no name duplicate found (exclude denied/declined)
    if (!existingUnit) {
      for (const docSnap of numberSnapshot.docs) {
        const data = docSnap.data();
        const reviewStatus = data.reviewStatus || 'approved';
        // Explicitly exclude denied units from duplicate check
        if (reviewStatus === 'approved' || reviewStatus === 'pending') {
          existingUnit = {
            id: docSnap.id,
            name: data.name as string,
            unitNumber: data.unitNumber as string,
            status: reviewStatus,
          };
          duplicateType = 'number';
          break;
        }
      }
    }

    if (existingUnit && duplicateType) {
      return {
        exists: true,
        unit: existingUnit,
        duplicateType,
      };
    }

    return { exists: false };
  } catch (error) {
    console.error('Error checking unit duplicate:', error);
    throw error;
  }
}

/**
 * Create a new unit with pending review status
 */
export async function createUnit(
  db: Firestore,
  data: UnitCreationData
): Promise<string> {
  try {
    const platformSnap = await getDoc(doc(db, 'systemSettings', 'platform'));
    const startingCredits = parseDefaultCreditBalanceOnUnitCreate(
      platformSnap.exists() ? (platformSnap.data() as Record<string, unknown>) : undefined,
    );
    const startingCreditFields =
      startingCredits > 0
        ? { creditBalance: startingCredits, creditBalanceUpdatedAt: new Date() }
        : {};

    const unitData = {
      name: data.name.trim(),
      unitNumber: data.unitNumber.trim(),
      number: data.unitNumber.trim(),
      members: [], // Don't add members until approved
      createdAt: new Date(),
      createdBy: data.userId,
      createdByEmail: data.userEmail,
      termsAcceptedAt: data.termsAcceptedAt,
      reviewStatus: 'pending',
      reviewData: {
        submittedAt: new Date(),
        submittedBy: data.userId,
        submittedByEmail: data.userEmail,
      },
      subscription: {
        status: 'trial',
        trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
        billingEmail: data.userEmail,
      },
      settings: {},
      ...data.additionalData,
      ...startingCreditFields,
    };

    const unitRef = await addDoc(collection(db, 'units'), unitData);
    return unitRef.id;
  } catch (error) {
    console.error('Error creating unit:', error);
    throw error;
  }
}

export interface JoinableUnit {
  id: string;
  name: string;
  unitNumber: string;
  typeId?: string;
  reviewStatus: string;
}

export interface MembershipRequestCheck {
  hasPending: boolean;
  requestId?: string;
}

function mapJoinableUnitDoc(
  docSnap: { id: string; data: () => Record<string, unknown> }
): JoinableUnit | null {
  const data = docSnap.data();
  const reviewStatus = (data.reviewStatus as string) || 'approved';

  if (reviewStatus !== 'approved') {
    return null;
  }

  return {
    id: docSnap.id,
    name: data.name as string,
    unitNumber: (data.unitNumber || data.number) as string,
    typeId: data.typeId as string | undefined,
    reviewStatus,
  };
}

/**
 * Find an approved unit by exact name, number, and type.
 * Queries both unitNumber (canonical) and legacy number fields.
 */
export async function findUnitForJoin(
  db: Firestore,
  unitName: string,
  unitNumber: string,
  typeId: string
): Promise<JoinableUnit | null> {
  const unitsRef = collection(db, 'units');
  const trimmedName = unitName.trim();
  const trimmedNumber = unitNumber.trim();

  for (const numberField of ['unitNumber', 'number'] as const) {
    const unitQuery = query(
      unitsRef,
      where('name', '==', trimmedName),
      where(numberField, '==', trimmedNumber),
      where('typeId', '==', typeId)
    );

    const snapshot = await getDocs(unitQuery);
    if (!snapshot.empty) {
      return mapJoinableUnitDoc(snapshot.docs[0]);
    }
  }

  return null;
}

/**
 * Check if user already has a pending membership request for a unit
 */
export async function checkPendingMembershipRequest(
  db: Firestore,
  unitId: string,
  userId: string
): Promise<MembershipRequestCheck> {
  const pendingQuery = query(
    collection(db, 'membershipRequests'),
    where('unitId', '==', unitId),
    where('userId', '==', userId),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(pendingQuery);
  if (snapshot.empty) {
    return { hasPending: false };
  }

  return { hasPending: true, requestId: snapshot.docs[0].id };
}

/**
 * Request membership to an existing unit
 */
export async function requestUnitMembership(
  db: Firestore,
  existingUnitId: string,
  existingUnitName: string,
  userId: string,
  userEmail: string,
  attemptedUnitName: string,
  attemptedUnitNumber: string,
  requestType: 'duplicate_attempt' | 'direct_request' = 'duplicate_attempt'
): Promise<void> {
  try {
    const existing = await checkPendingMembershipRequest(db, existingUnitId, userId);
    if (existing.hasPending) {
      throw new Error('You already have a pending membership request for this unit');
    }

    await addDoc(collection(db, 'membershipRequests'), {
      unitId: existingUnitId,
      unitName: existingUnitName,
      userId,
      userEmail,
      status: 'pending',
      createdAt: Timestamp.now(),
      requestType,
      attemptedUnitName,
      attemptedUnitNumber,
    });
  } catch (error) {
    console.error('Error creating membership request:', error);
    throw error;
  }
}

/**
 * Report an unauthorized unit creation
 */
export async function reportUnauthorizedUnit(
  db: Firestore,
  reportedUnitId: string,
  reportedUnitName: string,
  reporterId: string,
  reporterEmail: string,
  reason: string,
  attemptedUnitName: string,
  attemptedUnitNumber: string
): Promise<void> {
  try {
    await addDoc(collection(db, 'unitReports'), {
      reportedUnitId,
      reportedUnitName,
      reporterId,
      reporterEmail,
      reason,
      status: 'pending',
      createdAt: new Date(),
      attemptedUnitName,
      attemptedUnitNumber,
    });
  } catch (error) {
    console.error('Error submitting report:', error);
    throw error;
  }
}

